import { useState } from 'react'
import { Play, Calendar, Clock } from 'lucide-react'
import { VideoPlayer } from './VideoPlayer'

interface Video {
  id: string
  title: string
  thumbnail: string
  channel_title: string
  channel_id: string
  published_at: string
  duration: string
  description?: string
}

interface VideoCardProps {
  video: Video
}

export function VideoCard({ video }: VideoCardProps) {
  const [showPlayer, setShowPlayer] = useState(false)

  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    })
  }

  const formatDuration = (duration: string) => {
    // Convert ISO 8601 duration to readable format
    const match = duration.match(/PT(\d+H)?(\d+M)?(\d+S)?/)
    if (!match) return ''
    
    const hours = match[1] ? parseInt(match[1]) : 0
    const minutes = match[2] ? parseInt(match[2]) : 0
    const seconds = match[3] ? parseInt(match[3]) : 0
    
    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`
    }
    return `${minutes}:${seconds.toString().padStart(2, '0')}`
  }

  return (
    <>
      <div className="bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden group cursor-pointer transform hover:scale-105">
        <div className="relative" onClick={() => setShowPlayer(true)}>
          <img
            src={video.thumbnail}
            alt={video.title}
            className="w-full h-48 object-cover"
          />
          <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all duration-300 flex items-center justify-center">
            <div className="bg-red-500 rounded-full p-3 transform scale-0 group-hover:scale-100 transition-transform duration-300">
              <Play className="w-8 h-8 text-white fill-white" />
            </div>
          </div>
          <div className="absolute bottom-2 right-2 bg-black bg-opacity-75 text-white px-2 py-1 rounded text-xs font-medium">
            <Clock className="w-3 h-3 inline mr-1" />
            {formatDuration(video.duration)}
          </div>
        </div>
        
        <div className="p-4">
          <h3 className="font-bold text-gray-800 text-lg leading-tight mb-2 line-clamp-2">
            {video.title}
          </h3>
          
          <div className="flex items-center text-gray-600 text-sm mb-2">
            <span className="font-medium text-blue-600">{video.channel_title}</span>
          </div>
          
          <div className="flex items-center text-gray-500 text-xs">
            <Calendar className="w-3 h-3 mr-1" />
            {formatDate(video.published_at)}
          </div>
        </div>
      </div>

      {showPlayer && (
        <VideoPlayer
          videoId={video.id}
          title={video.title}
          onClose={() => setShowPlayer(false)}
        />
      )}
    </>
  )
}